<script setup lang="ts">
import { computed, type PropType } from 'vue'
import type { GreenGladeType } from '@/shared/ui'
import { useHelpers, useTranslate } from '@/shared/lib'
import { DateTime } from 'ts-luxon'

const props = defineProps({
  item: {
    type: Object as PropType<GreenGladeType>,
    required: true,
  },
  from: {
    type: String,
    required: true,
  },
  to: {
    type: String,
    required: true,
  },
})

const { toTime } = useHelpers()
const { trs } = useTranslate()

/**
 * Вычисляем значение для секций
 */
const sections = computed(() => {
  if (!props.item?.list?.length) return []

  const fromMillis = toTime(props.from).toMillis()
  const toMillis = toTime(props.to).toMillis()
  const total = toMillis - fromMillis
  const items: any[] = []

  let lastTime = fromMillis
  let lastValue = Number(props.item.list[0].value) ? 0 : 1

  /**
   * Функция добавления элемента списка
   * @param time
   * @param value
   */
  const addItem = (time: number, value: number) => {
    const percent = (((time - lastTime) / total) * 100).toFixed(2)
    items.push({
      percent,
      value: lastValue,
      label: getLabel(props.item.name, lastValue, lastTime, time),
    })
    lastTime = time
    lastValue = value
  }

  props.item.list.forEach((item) => {
    addItem(toTime(item.created_at).toMillis(), Number(item.value))
  })

  addItem(toMillis, lastValue)

  return items
})

/**
 * Функция получения значение заголовка
 * @param name
 * @param value
 * @param from
 * @param to
 */
const getLabel = (name: string, value: number, from: number, to: number) => {
  const fromTime = DateTime.fromMillis(from).toFormat('dd.MM.yyyy HH:mm')
  const toTime = DateTime.fromMillis(to).toFormat('dd.MM.yyyy HH:mm')

  let label = trs('itf.green_glade_label', {
    name,
    status: trs(`itf.green_glade_status.${value ? 'online' : 'offline'}`),
  })

  label += '</br>'
  label += trs('itf.green_glade_time', {
    from: fromTime,
    to: toTime,
  })

  return label
}
</script>

<template>
  <div class="v-green-glade__row">
    <div class="v-green-glade__name">
      <VTooltip :text="item.name" location="top">
        <template v-slot:activator="{ props }">
          <span v-bind="props">{{ item.name }}</span>
        </template>
      </VTooltip>
    </div>

    <div class="v-green-glade__sections">
      <template v-for="section in sections">
        <VTooltip location="top" interactive>
          <template v-slot:activator="{ props }">
            <div
              v-bind="props"
              class="v-green-glade__section"
              :style="{ width: section.percent + '%' }"
              :class="`v-green-glade__section_${section.value ? 'online' : 'offline'}`"
            ></div>
          </template>

          <div class="text-center" v-html="section.label"></div>
        </VTooltip>
      </template>
    </div>
  </div>
</template>
